// Data groups: point groups with a number per point, made and worked on by a macro's data steps (macros.ts Data
// group, Data math, Data edit; Data band selects by them) and shown by the Data shading. Like memory groups
// (memgroups.ts) the page keeps them (by scan, then name) until it is closed, never in the scan's log. A group per
// cloud is one Float32Array in vertex order: NaN where a point isn't in it, which reads as the group's default.
// Never changed in place, so a copy of the map is a snapshot. Pure.

/** A data group over a run's clouds: its default (what a point not in it reads as) and per cloud its values (null: none there). */
export interface Data { dflt: number; v: (Float32Array | null)[] }
/** A run's data groups by name. */
export type DataSets = Record<string, Data>;
/** One scan's data groups, as the page keeps them: name -> default and values. */
export interface DGroup { dflt: number; v: Float32Array }
export type DataGroups = Map<string, DGroup>;

export type DataOp = 'set' | 'add' | 'sub' | 'mul' | 'div' | 'min' | 'max';
export type Curve = 'linear' | 'smooth' | 'gauss' | 'inverse';

/** Is the point in the group (a value, not NaN)? */
export const isIn = (x: number) => x === x;

/** What the group's point i reads as: its value, or the default. */
export const valueAt = (d: Data, k: number, i: number) => { const v = d.v[k]?.[i]; return v === undefined || v !== v ? d.dflt : v; };

/** a op b; NaN for a division by 0 (the point is left as it was). */
export function combine(op: DataOp, a: number, b: number): number {
  switch (op) {
    case 'set': return b;
    case 'add': return a + b;
    case 'sub': return a - b;
    case 'mul': return a * b;
    case 'div': return b === 0 ? NaN : a / b;
    case 'min': return Math.min(a, b);
    case 'max': return Math.max(a, b);
  }
}

/**
 * The group's values after `op` with the source values src on its points that `on` takes (null: all its points);
 * a point whose source is unknown (NaN), or whose result isn't a finite number, keeps its value. null: no change
 * (the arrays it didn't change are shared). n: how many points changed. only: per cloud, the only points to look at
 * (null: none of that cloud's), in place of going over them all.
 */
export function applyData(d: Data, op: DataOp, src: (k: number, i: number) => number, on: ((k: number, i: number) => boolean) | null, only?: (ArrayLike<number> | null)[]): { d: Data; n: number } | null {
  let n = 0;
  const v = d.v.map((a, k) => {
    if (!a) return a;
    let o: Float32Array | null = null;
    const ix = only ? only[k] : null, len = only ? ix?.length ?? 0 : a.length;
    for (let j = 0; j < len; j++) {
      const i = ix ? ix[j] : j, x = a[i];
      if (x !== x || (on && !on(k, i))) continue;
      const s = src(k, i);
      if (s !== s) continue;
      const y = Math.fround(combine(op, x, s));
      if (!Number.isFinite(y) || y === x) continue;
      (o ??= a.slice())[i] = y;
      n++;
    }
    return o ?? a;
  });
  return n ? { d: { dflt: d.dflt, v }, n } : null;
}

/** A group of the points of masks (per cloud), each its value from val (NaN: the default), with this default. */
export function newData(masks: (Uint8Array | null)[], dflt: number, val: (k: number, i: number) => number): Data {
  return {
    dflt, v: masks.map((m, k) => {
      if (!m || !m.some(x => x)) return null;
      const o = new Float32Array(m.length).fill(NaN);
      for (let i = 0; i < m.length; i++) if (m[i]) { const x = val(k, i); o[i] = x === x && Number.isFinite(x) ? x : dflt; }
      return o;
    }),
  };
}

/** The group with the points of masks put in (at its default; those in it already keep their values) or taken out. null: no change. */
export function dataMembers(d: Data, masks: (Uint8Array | null)[], add: boolean): { d: Data; n: number } | null {
  let n = 0;
  const v = d.v.map((a, k) => {
    const m = masks[k];
    if (!m) return a;
    let o: Float32Array | null = null;
    for (let i = 0; i < m.length; i++) {
      if (!m[i]) continue;
      const x = a ? a[i] : NaN;
      if (add ? x === x : x !== x) continue;
      o ??= a ? a.slice() : new Float32Array(m.length).fill(NaN);
      o[i] = add ? d.dflt : NaN;
      n++;
    }
    if (!o) return a;
    return o.some(isIn) ? o : null;
  });
  return n ? { d: { dflt: d.dflt, v }, n } : null;
}

/** The points in the group, per cloud (null: none there). */
export const dataMask = (d: Data): (Uint8Array | null)[] => d.v.map(a => (a ? Uint8Array.from(a, x => (x === x ? 1 : 0)) : null));

/** Points in a group's values. */
export const dataCount = (a: Float32Array | null | undefined) => { let n = 0; if (a) for (let i = 0; i < a.length; i++) if (a[i] === a[i]) n++; return n; };

/**
 * The 2nd and 98th percentiles of the group's values (every cloud's together), and the least and most: the ends
 * the Data shading's ramp can take (auto: least to most; or clipped, the few outliers past the percentiles left at
 * the ends). null: no points in it.
 */
export function dataRange(vs: (Float32Array | null)[]): { lo: number; hi: number; min: number; max: number } | null {
  let n = 0, min = Infinity, max = -Infinity;
  for (const a of vs) if (a) for (let i = 0; i < a.length; i++) { const x = a[i]; if (x === x) { n++; if (x < min) min = x; if (x > max) max = x; } }
  if (!n) return null;
  if (!(max > min)) return { lo: min, hi: max, min, max };
  // the percentiles from a histogram of every value (to a 4096th of the spread): no sample to miss a few changed points
  const B = 4096, h = new Uint32Array(B), w = (max - min) / B;
  for (const a of vs) if (a) for (let i = 0; i < a.length; i++) { const x = a[i]; if (x === x) h[Math.min(B - 1, Math.floor((x - min) / w))]++; }
  const at = (q: number) => { const want = q * (n - 1); let c = 0; for (let b = 0; b < B; b++) { c += h[b]; if (c > want) return min + b * w; } return max; };
  return { lo: at(0.02), hi: Math.min(max, at(0.98) + w), min, max };
}

/** Proximity at distance d of r: 1 touching to 0 at r and beyond (inverse: 0 touching to 1 at r and beyond). */
export function falloff(d: number, r: number, curve: Curve): number {
  const t = d >= r ? 1 : Math.max(0, d / r);
  switch (curve) {
    case 'linear': return 1 - t;
    case 'smooth': return 1 - t * t * (3 - 2 * t);
    case 'gauss': return t >= 1 ? 0 : Math.exp(-4.5 * t * t);   // sigma r/3
    case 'inverse': return t;
  }
}

/** A cloud's points for nearest: positions, which are sources, and which want their distance. */
export interface NearCloud { P: Float32Array; src: Uint8Array | null; dst: Uint8Array | null }

/**
 * For each dst point, the distance to the nearest src point of any cloud (they overlap: one cloud), Infinity when
 * none is within r. Points with no position (NaN) take no part. A kd-tree over the sources, searched within r: as
 * quick for a wide reach as a narrow one, and for points with no source near (most of them, often).
 */
export function nearest(clouds: NearCloud[], r: number): Float32Array[] {
  const out = clouds.map(c => new Float32Array(c.P.length / 3).fill(Infinity));
  let ns = 0;
  for (const c of clouds) if (c.src) for (let i = 0; i < c.src.length; i++) if (c.src[i] && c.P[3 * i] === c.P[3 * i]) ns++;
  if (!ns || !(r > 0)) return out;
  const S = new Float32Array(3 * ns);
  let o = 0;
  for (const c of clouds) if (c.src) for (let i = 0; i < c.src.length; i++) if (c.src[i] && c.P[3 * i] === c.P[3 * i]) { S[o++] = c.P[3 * i]; S[o++] = c.P[3 * i + 1]; S[o++] = c.P[3 * i + 2]; }
  const t = new KdTree(S), r2 = r * r;
  clouds.forEach((c, n) => {
    const dst = c.dst, P = c.P, res = out[n];
    if (!dst) return;
    for (let i = 0; i < dst.length; i++) {
      if (!dst[i] || P[3 * i] !== P[3 * i]) continue;
      const q = t.nearest2(P[3 * i], P[3 * i + 1], P[3 * i + 2], r2);
      if (q < r2) res[i] = Math.sqrt(q);
    }
  });
  return out;
}

/** A cloud for nearness: nearest's, and the sources' weights (per point, its value; null: every source 1). */
export interface WeightedCloud extends NearCloud { w?: Float32Array | null }

/**
 * For each dst point, its nearness to the source points of any cloud within r: the most any source gives, its
 * weight (clamped to at most 1; a source of 0 or less, or NaN, is none) times its falloff (the curve's proximity;
 * inverse: 1 less that most). With every weight 1 it is the falloff of the nearest source's distance. NaN where a
 * point is no dst, or no source is within r (beyond its reach).
 */
export function nearness(clouds: WeightedCloud[], r: number, curve: Curve): Float32Array[] {
  const out = clouds.map(c => new Float32Array(c.P.length / 3).fill(NaN));
  const weight = (c: WeightedCloud, i: number) => (c.src![i] && c.P[3 * i] === c.P[3 * i] ? (c.w ? Math.min(c.w[i], 1) : 1) : 0);
  let ns = 0;
  for (const c of clouds) if (c.src) for (let i = 0; i < c.src.length; i++) if (weight(c, i) > 0) ns++;
  if (!ns || !(r > 0)) return out;
  const S = new Float32Array(3 * ns), W = new Float32Array(ns);
  let o = 0;
  for (const c of clouds) if (c.src) for (let i = 0; i < c.src.length; i++) {
    const w = weight(c, i);
    if (w > 0) { S.set(c.P.subarray(3 * i, 3 * i + 3), 3 * o); W[o++] = w; }
  }
  const t = new KdTree(S, W), r2 = r * r, inv = curve === 'inverse', near: Curve = inv ? 'linear' : curve;
  const f = (d2: number) => falloff(Math.sqrt(d2), r, near);
  clouds.forEach((c, n) => {
    const dst = c.dst, P = c.P, res = out[n];
    if (!dst) return;
    for (let i = 0; i < dst.length; i++) {
      if (!dst[i] || P[3 * i] !== P[3 * i]) continue;
      const m = t.most(P[3 * i], P[3 * i + 1], P[3 * i + 2], r2, f);
      if (m >= 0) res[i] = inv ? 1 - m : m;
    }
  });
  return out;
}

const LEAF = 8;

/**
 * A kd-tree, implicit: the points reordered so each node [lo, hi) splits at its middle point (on the axis its box is
 * widest along, ax[mid]), the ones before it on the low side; nodes of LEAF points or fewer are searched through.
 */
export class KdTree {
  private P: Float32Array; private ax: Uint8Array;
  /** The points' weights (most), in the tree's order; null: none given (each 1). */
  private W: Float32Array | null = null;
  /** The points' bounds: min x y z, max x y z. */
  private box = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  constructor(P: Float32Array, W?: Float32Array) {
    const n = P.length / 3, idx = Uint32Array.from({ length: n }, (_, i) => i);
    this.ax = new Uint8Array(n);
    // (an explicit stack: deep trees of millions of points)
    const stack = [0, n];
    while (stack.length) {
      const hi = stack.pop()!, lo = stack.pop()!;
      if (hi - lo <= LEAF) continue;
      let a = 0, w = -1;
      for (let k = 0; k < 3; k++) {
        let mn = Infinity, mx = -Infinity;
        for (let j = lo; j < hi; j++) { const v = P[3 * idx[j] + k]; if (v < mn) mn = v; if (v > mx) mx = v; }
        if (mx - mn > w) { w = mx - mn; a = k; }
      }
      const mid = (lo + hi) >> 1;
      select(idx, P, a, lo, hi - 1, mid);
      this.ax[mid] = a;
      stack.push(lo, mid, mid + 1, hi);
    }
    this.P = new Float32Array(3 * n);
    for (let j = 0; j < n; j++) { const i = idx[j]; this.P[3 * j] = P[3 * i]; this.P[3 * j + 1] = P[3 * i + 1]; this.P[3 * j + 2] = P[3 * i + 2]; }
    if (W) { const w = (this.W = new Float32Array(n)); for (let j = 0; j < n; j++) w[j] = W[idx[j]]; }
    for (let j = 0; j < 3 * n; j++) { const k = j % 3, v = P[j]; if (v < this.box[k]) this.box[k] = v; if (v > this.box[k + 3]) this.box[k + 3] = v; }
  }

  /**
   * The squared distance from (x, y, z) to the nearest point, if it is under best (else best). A node is searched
   * only when its box is nearer than that: off holds the query's offset from the box along each axis, rd their
   * squares summed (Arya and Mount's incremental distance), so a flat set (a wall) is passed by from beside it.
   */
  nearest2(x: number, y: number, z: number, best: number): number {
    const P = this.P, ax = this.ax, q = [x, y, z], b = this.box, off = [0, 0, 0];
    for (let k = 0; k < 3; k++) off[k] = q[k] < b[k] ? q[k] - b[k] : q[k] > b[k + 3] ? q[k] - b[k + 3] : 0;
    const go = (lo: number, hi: number, rd: number) => {
      if (hi - lo <= LEAF) {
        for (let j = lo; j < hi; j++) {
          const dx = P[3 * j] - x, dy = P[3 * j + 1] - y, dz = P[3 * j + 2] - z, d = dx * dx + dy * dy + dz * dz;
          if (d < best) best = d;
        }
        return;
      }
      const mid = (lo + hi) >> 1, a = ax[mid], d = q[a] - P[3 * mid + a];
      const dx = P[3 * mid] - x, dy = P[3 * mid + 1] - y, dz = P[3 * mid + 2] - z, dm = dx * dx + dy * dy + dz * dz;
      if (dm < best) best = dm;
      // the near side has the same box offset; the far side's is at least d along a
      const [nlo, nhi, flo, fhi] = d < 0 ? [lo, mid, mid + 1, hi] : [mid + 1, hi, lo, mid];
      go(nlo, nhi, rd);
      const old = off[a], rf = rd - old * old + d * d;
      if (rf < best) { off[a] = d; go(flo, fhi, rf); off[a] = old; }
    };
    let rd = 0;
    for (const v of off) rd += v * v;
    if (rd < best) go(0, P.length / 3, rd);
    return best;
  }
  /**
   * The most a point within r (r2 its square) gives at (x, y, z): its weight times f of its squared distance (f
   * falling, at most 1, as weights are). -1: none within r. A node is passed by when its box is r or further, or
   * nearer than that yet f there is no more than the best so far (no weight makes it more); as nearest2 otherwise.
   */
  most(x: number, y: number, z: number, r2: number, f: (d2: number) => number): number {
    const P = this.P, W = this.W, ax = this.ax, q = [x, y, z], b = this.box, off = [0, 0, 0];
    let best = -1;
    const at = (j: number) => {
      const dx = P[3 * j] - x, dy = P[3 * j + 1] - y, dz = P[3 * j + 2] - z, d = dx * dx + dy * dy + dz * dz;
      if (d < r2) { const v = (W ? W[j] : 1) * f(d); if (v > best) best = v; }
    };
    const worth = (rd: number) => rd < r2 && f(rd) > best;
    for (let k = 0; k < 3; k++) off[k] = q[k] < b[k] ? q[k] - b[k] : q[k] > b[k + 3] ? q[k] - b[k + 3] : 0;
    const go = (lo: number, hi: number, rd: number) => {
      if (hi - lo <= LEAF) { for (let j = lo; j < hi; j++) at(j); return; }
      const mid = (lo + hi) >> 1, a = ax[mid], d = q[a] - P[3 * mid + a];
      at(mid);
      const [nlo, nhi, flo, fhi] = d < 0 ? [lo, mid, mid + 1, hi] : [mid + 1, hi, lo, mid];
      go(nlo, nhi, rd);
      const old = off[a], rf = rd - old * old + d * d;
      if (worth(rf)) { off[a] = d; go(flo, fhi, rf); off[a] = old; }
    };
    let rd = 0;
    for (const v of off) rd += v * v;
    if (worth(rd)) go(0, P.length / 3, rd);
    return best;
  }
}

/** Reorder idx[lo..hi] so idx[k] is the point with the k-th smallest coordinate a, the smaller before it (quickselect). */
function select(idx: Uint32Array, P: Float32Array, a: number, lo: number, hi: number, k: number) {
  while (hi > lo) {
    const pv = P[3 * idx[(lo + hi) >> 1] + a];
    let i = lo, j = hi;
    while (i <= j) {
      while (P[3 * idx[i] + a] < pv) i++;
      while (P[3 * idx[j] + a] > pv) j--;
      if (i <= j) { const t = idx[i]; idx[i] = idx[j]; idx[j] = t; i++; j--; }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else return;
  }
}

// ── the page's side: a scan's data groups, and a run's from them ──
/** The data groups of scans (in a run's order) as a run takes them: every name any of them has. */
export function dataSets(groups: DataGroups[]): DataSets {
  const out: DataSets = {};
  groups.forEach((g, k) => {
    for (const [name, x] of g) {
      const d = (out[name] ??= { dflt: x.dflt, v: groups.map(() => null) });
      d.v[k] = x.v;
    }
  });
  return out;
}

/**
 * One scan's data groups after a run step changed some (changed: name -> the group over the run's clouds, null:
 * deleted; k: the scan's place in the run). null: no change to this scan's.
 */
export function dataLanded(g: DataGroups, changed: Record<string, Data | null>, k: number): DataGroups | null {
  let out: DataGroups | null = null;
  for (const [name, d] of Object.entries(changed)) {
    const a = d?.v[k] ?? null, cur = g.get(name);
    if (!a) { if (cur) (out ??= new Map(g)).delete(name); continue; }
    if (cur && cur.v === a && cur.dflt === d!.dflt) continue;
    (out ??= new Map(g)).set(name, { dflt: d!.dflt, v: a });
  }
  return out;
}
